import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdDocumentsByDocumentIdentifierOptions } from '@onefootprint/fetch-dashboard';
import { IcoCheckSmall16, IcoChevronDown16, IcoChevronLeftBig16, IcoSidebarLeftClosed16, IcoSidebarLeftOpen16 } from '@onefootprint/icons';
import { getErrorMessage } from '@onefootprint/request';
import type { DataIdentifier, PiiJsonValue } from '@onefootprint/request-types/dashboard';
import { DialogPage, IconButton, LoadingSpinner } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { format } from 'date-fns';
import partition from 'lodash/partition';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CenterPanel from './components/center-panel';
import DocumentPreview from './components/document-preview';
import LeftSidebar from './components/left-sidebar';
import ViewerLayout from './components/viewer-layout';
import { useViewerState } from './hooks';
type DecryptKind = 'document';
type DecryptedData = Record<string, PiiJsonValue>;
export type DocumentUpload = {
  side: 'front' | 'back' | 'selfie' | 'selfie_video' | 'document';
  timestamp: string;
  failureReasons: string[];
  isLatest: boolean;
  imageData: string | null;
};
type DocumentViewerProps = {
  documentSource: string | null;
  documentIdentifier: string;
  documentName: string;
  fpId: string;
  onBack: () => void;
  onDecrypt?: (kind: DecryptKind) => Promise<void>;
  onDecryptVaultData?: (fields: DataIdentifier[]) => Promise<DecryptedData | null>;
  uploads?: DocumentUpload[];
};
const DocumentViewer = t0 => {
  const $ = _c(64);
  const {
    fpId,
    documentIdentifier,
    documentName,
    documentSource,
    onBack,
    onDecrypt,
    onDecryptVaultData,
    uploads
  } = t0;
  const {
    t
  } = useTranslation("document-viewer");
  const [selectedUploadIndex, setSelectedUploadIndex] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  let t1;
  if ($[0] !== documentIdentifier || $[1] !== fpId) {
    t1 = getEntitiesByFpIdDocumentsByDocumentIdentifierOptions({
      path: {
        fpId,
        documentIdentifier
      }
    });
    $[0] = documentIdentifier;
    $[1] = fpId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !!fpId && !!documentIdentifier;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data,
    isPending,
    error
  } = useQuery(t3);
  let t4;
  if ($[6] !== data?.cards) {
    t4 = data?.cards ?? [];
    $[6] = data?.cards;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = {
      cards: t4
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    selectedTab,
    setSelectedTab,
    selectedBboxIds,
    toggleBboxSelection,
    selectBboxes,
    clearBboxes,
    groupedCards,
    scrollToPageId,
    isCenterPanelOpen,
    toggleCenterPanel,
    hasAnyContent
  } = useViewerState(t5);
  let t6;
  bb0: {
    if (!uploads) {
      t6 = [];
      break bb0;
    }
    const [successfulUploads, failedUploads] = partition(uploads, _temp);
    t6 = [...successfulUploads.sort(_temp2), ...failedUploads.sort(_temp3)];
  }
  const sortedUploads = t6;
  const safeSelectedIndex = sortedUploads.length > 0 ? Math.min(selectedUploadIndex, sortedUploads.length - 1) : 0;
  const selectedUpload = sortedUploads[safeSelectedIndex];
  const showDropdown = sortedUploads.length > 1;
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const handleClickOutside = event => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  const handleSelectUpload = index => {
    setSelectedUploadIndex(index);
    setIsDropdownOpen(false);
  };
  let t9;
  if ($[12] !== t) {
    t9 = (upload_0, t10) => {
      const showFullDate = t10 === undefined ? false : t10;
      const isSuccess = upload_0.failureReasons.length === 0;
      const statusText = isSuccess ? t(_temp4) : t(_temp5);
      const dateFormat = showFullDate ? "MM/dd/yyyy, hh:mm a" : "hh:mm a";
      const formattedDate = format(new Date(upload_0.timestamp), dateFormat);
      return {
        isSuccess,
        statusText,
        formattedDate
      };
    };
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const getUploadStatusLabel = t9;
  const T0 = DialogPage;
  const t10 = true;
  const t11 = true;
  const t12 = false;
  const t13 = "flex flex-col h-full w-full";
  const t14 = "flex flex-wrap gap-2 items-center justify-between px-3 py-2 border-b border-tertiary bg-primary shrink-0";
  let t15;
  if ($[14] !== t) {
    t15 = t(_temp6);
    $[14] = t;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== onBack || $[17] !== t15) {
    t16 = <IconButton icon={IcoChevronLeftBig16} aria-label={t15} onClick={onBack} />;
    $[16] = onBack;
    $[17] = t15;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== hasAnyContent || $[20] !== isCenterPanelOpen || $[21] !== t || $[22] !== toggleCenterPanel) {
    t17 = hasAnyContent && <><div className="w-px h-4 bg-(--border-color-tertiary)" /><IconButton icon={isCenterPanelOpen ? IcoSidebarLeftOpen16 : IcoSidebarLeftClosed16} aria-label={isCenterPanelOpen ? t(_temp7) : t(_temp8)} onClick={toggleCenterPanel} /></>;
    $[19] = hasAnyContent;
    $[20] = isCenterPanelOpen;
    $[21] = t;
    $[22] = toggleCenterPanel;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== t16 || $[25] !== t17) {
    t18 = <div className="flex items-center gap-1">{t16}{t17}</div>;
    $[24] = t16;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const t19 = selectedUpload && <div className="relative" ref={dropdownRef}><button type="button" onClick={() => showDropdown && setIsDropdownOpen(!isDropdownOpen)} className={cx("flex items-center gap-1", showDropdown && "cursor-pointer")} disabled={!showDropdown}><span className="text-label-2"><span className={getUploadStatusLabel(selectedUpload).isSuccess ? "text-success" : "text-error"}>{getUploadStatusLabel(selectedUpload).statusText}</span><span className="text-primary"> · {getUploadStatusLabel(selectedUpload).formattedDate}</span></span>{showDropdown && <IcoChevronDown16 className={cx("text-tertiary transition-transform", isDropdownOpen && "rotate-180")} />}</button>{isDropdownOpen && <div className="absolute top-full right-0 md:right-auto md:left-1/2 md:-translate-x-1/2 mt-2 bg-primary border border-tertiary rounded-md shadow-md z-10 min-w-[280px] max-md:max-w-[calc(100vw-24px)]"><div className="py-1">{sortedUploads.map((upload_1, index_0) => {
          const isSelected = index_0 === safeSelectedIndex;
          const {
            isSuccess: isSuccess_0,
            statusText: statusText_0,
            formattedDate: formattedDate_0
          } = getUploadStatusLabel(upload_1, true);
          return <button key={`${upload_1.side}-${upload_1.timestamp}`} type="button" onClick={() => handleSelectUpload(index_0)} className={cx("w-full flex flex-wrap gap-2 items-center justify-between px-3 py-2 text-left cursor-pointer", "hover:bg-hover", isSelected && "bg-senary/70")}><span className="text-body-2"><span className={isSuccess_0 ? "text-success" : "text-error"}>{statusText_0}</span><span className="text-primary"> · {formattedDate_0}</span></span>{isSelected && <IcoCheckSmall16 className="text-primary shrink-0" />}</button>;
        })}</div></div>}</div>;
  let t20;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="hidden md:block w-[76px]" />;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== t18 || $[29] !== t19) {
    t21 = <header className={t14}>{t18}{t19}{t20}</header>;
    $[28] = t18;
    $[29] = t19;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== isPending) {
    t22 = isPending && <div className="flex items-center justify-center h-full w-full"><LoadingSpinner /></div>;
    $[31] = isPending;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== error) {
    t23 = error && <div className="flex items-center justify-center h-full w-full"><p className="text-body-2 text-error">{getErrorMessage(error)}</p></div>;
    $[33] = error;
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  let t24;
  if ($[35] !== clearBboxes || $[36] !== data || $[37] !== documentIdentifier || $[38] !== documentName || $[39] !== documentSource || $[40] !== groupedCards || $[41] !== isCenterPanelOpen || $[42] !== onDecrypt || $[43] !== onDecryptVaultData || $[44] !== scrollToPageId || $[45] !== selectBboxes || $[46] !== selectedBboxIds || $[47] !== selectedTab || $[48] !== selectedUpload || $[49] !== setSelectedTab || $[50] !== toggleBboxSelection) {
    t24 = data && <ViewerLayout leftSidebar={<LeftSidebar scores={data.scores} groupedCards={groupedCards} selectedTab={selectedTab} onTabChange={setSelectedTab} />} centerPanel={<CenterPanel cards={groupedCards[selectedTab]} selectedTab={selectedTab} selectedBboxIds={selectedBboxIds} onBboxClick={toggleBboxSelection} onSelectBboxes={selectBboxes} onClearBboxes={clearBboxes} isEncrypted={documentSource === null} onDecryptVaultData={onDecryptVaultData} documentIdentifier={documentIdentifier} />} documentPreview={<div className="flex-1 min-w-0 min-h-0 overflow-hidden bg-primary p-4"><DocumentPreview documentSource={documentSource} cards={groupedCards[selectedTab]} selectedBboxIds={selectedBboxIds} onBboxClick={toggleBboxSelection} scrollToPageId={scrollToPageId ?? undefined} onDecrypt={onDecrypt ? async () => onDecrypt("document") : undefined} selectedUpload={selectedUpload} documentName={documentName} /></div>} isCenterPanelOpen={isCenterPanelOpen} />;
    $[35] = clearBboxes;
    $[36] = data;
    $[37] = documentIdentifier;
    $[38] = documentName;
    $[39] = documentSource;
    $[40] = groupedCards;
    $[41] = isCenterPanelOpen;
    $[42] = onDecrypt;
    $[43] = onDecryptVaultData;
    $[44] = scrollToPageId;
    $[45] = selectBboxes;
    $[46] = selectedBboxIds;
    $[47] = selectedTab;
    $[48] = selectedUpload;
    $[49] = setSelectedTab;
    $[50] = toggleBboxSelection;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t22 || $[53] !== t23 || $[54] !== t24) {
    t25 = <div className="flex-1 overflow-hidden">{t22}{t23}{t24}</div>;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t21 || $[57] !== t25) {
    t26 = <div className={t13}>{t21}{t25}</div>;
    $[56] = t21;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== T0 || $[60] !== documentName || $[61] !== onBack || $[62] !== t26) {
    t27 = <T0 title={documentName} noPadding={t10} noScroll={t11} onClose={onBack} showHeader={t12}>{t26}</T0>;
    $[59] = T0;
    $[60] = documentName;
    $[61] = onBack;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  return t27;
};
export default DocumentViewer;
function _temp(upload) {
  return upload.failureReasons.length === 0;
}
function _temp2(a, b) {
  return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
}
function _temp3(a_0, b_0) {
  return new Date(b_0.timestamp).getTime() - new Date(a_0.timestamp).getTime();
}
function _temp4($) {
  return $.uploads.success;
}
function _temp5($_0) {
  return $_0.uploads.failed;
}
function _temp6($_1) {
  return $_1.header.goBack;
}
function _temp7($_2) {
  return $_2.header.hideDetailsPanel;
}
function _temp8($_3) {
  return $_3.header.showDetailsPanel;
}